// imports
import React, { useRef } from 'react';
import { Box, TextField, InputAdornment } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { FOCUS_BLUE } from '../../constants/colors';

interface CustomInputProps {
  value: string | number | '';
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  width: string;
  inputWidth?: string;
  prefix?: string;
  suffix?: string;
  type?: 'text' | 'number';
  min?: string;
  max?: string;
  maxLength?: number;
  'data-testid'?: string;
  'aria-label'?: string;
  placeholder?: string;
}

export default function CustomInput({
  value,
  onChange,
  onKeyDown,
  width,
  inputWidth,
  prefix,
  suffix,
  type = 'text',
  min,
  max,
  maxLength,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  placeholder,
}: CustomInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const stepValue = (direction: 'up' | 'down') => {
    const input = inputRef.current;
    if (!input) return;
    if (direction === 'up') input.stepUp();
    else input.stepDown();
    input.dispatchEvent(new Event('input', { bubbles: true }));
  };

  const spinnerButtons = type === 'number' ? (
    <InputAdornment position="end" sx={{ ml: 0 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', mr: 0.5 }}>
        <Box
          component="button"
          type="button"
          tabIndex={-1}
          aria-label="Increase"
          onClick={() => stepValue('up')}
          sx={{
            all: 'unset',
            cursor: 'pointer',
            display: 'flex',
            lineHeight: 0,
            color: '#555',
            '&:hover': { color: '#000' },
          }}
        >
          <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box
          component="button"
          type="button"
          tabIndex={-1}
          aria-label="Decrease"
          onClick={() => stepValue('down')}
          sx={{
            all: 'unset',
            cursor: 'pointer',
            display: 'flex',
            lineHeight: 0,
            color: '#555',
            '&:hover': { color: '#000' },
          }}
        >
          <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
        </Box>
      </Box>
    </InputAdornment>
  ) : undefined;

  const endAdornment = suffix || spinnerButtons ? (
    <>
      {spinnerButtons}
      {suffix && (
        <InputAdornment position="end" sx={{ ml: 0 }}>
          {suffix}
        </InputAdornment>
      )}
    </>
  ) : undefined;

  return (
    <TextField
      data-testid={dataTestId}
      value={value}
      onChange={onChange}
      onKeyDown={onKeyDown}
      type={type}
      placeholder={placeholder}
      size="small"
      variant="outlined"
      inputRef={inputRef}
      slotProps={{
        input: {
          startAdornment: prefix ? (
            <InputAdornment position="start" sx={{ mr: 0 }}>
              {prefix}
            </InputAdornment>
          ) : undefined,
          endAdornment,
          sx: {
            fontSize: '0.875rem',
            height: 36,
            '& input[type=number]': {
              MozAppearance: 'textfield',
            },
            '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
              display: 'none',
            },
            ...(inputWidth && {
              '& input': { width: inputWidth },
            }),
          },
        },
        htmlInput: {
          'aria-label': ariaLabel,
          min,
          max,
          maxLength,
        },
      }}
      sx={{
        width,
        '& .MuiOutlinedInput-root': {
          '&:focus-within': {
            outline: `0.25rem solid ${FOCUS_BLUE}`,
            outlineOffset: '0px',
          },
        },
        '& .MuiOutlinedInput-input:focus': {
          outline: 'none',
        },
      }}
    />
  );
}
